import { AuditTrailAction } from '../../audit/domain/auditTrailAction';
import { DashboardComponentType } from '../../dashboards/domain/dashboardComponentType';
import { LogSorterColumnPlacement } from '../../logSorter/domain/logSorterBinLayout';
import { LogSorterNumberSequence } from '../../logSorter/domain/logSorterNumberSequence';
import { Anchor } from '../../map/domain/anchor';
import { ProductType } from '../../products/domain/productType';
import { ShapeMode } from '../../shapes/domain/shapeMode';
import { StorageAreaStatus } from '../../storageAreas/domain/storageAreaStatus';
import { StorageAreaType } from '../../storageAreas/domain/storageAreaType';
import { VehicleType } from '../../vehicles/domain/vehicleType';
import { VehicleSymbol } from '../../vehicles/domain/vehicleSymbol';
import { KeyboardActions } from '../../vehicles/domain/keyboardActions';
import { type Translations } from '../domain/translations';
import { ProductionOrderState } from '../../productionOrders/domain/productionOrderState';
import { ReportType } from '../../reports/domain/reportType';
import { Unit } from '../../common/domain/bulkUnits';

export const swedish: Translations = {
  generic: {
    active: 'Aktiv',
    language: 'Språk',
    yes: 'Ja',
    no: 'Nej',
    save: 'Spara',
    cancel: 'Avbryt',
    delete: 'Ta bort',
    id: 'Id',
    name: 'Namn',
    description: 'Beskrivning',
    type: 'Typ',
    code: 'Kod',
    quality: 'Kvalitet',
    columns: 'Kolumner',
    color: 'Färg',
    capacity: 'Kapacitet',
    loadingTableData: 'Laddar...',
    search: 'Sök',
    generalErrorMsg: 'Hoppsan! Något blev fel',
    signIn: 'Logga in',
    signOut: 'Logga ut',
    width: 'Bredd',
    length: 'Längd',
    height: 'Höjd',
    login: 'Logga in',
    logout: 'Logga ut',
    toggle: 'Växla',
    status: 'Status',
    next: 'Nästa',
    back: 'Tillbaka',
    abort: 'Avbryt',
    close: 'Stäng',
    searchBy: 'Sök på',
    clear: 'Rensa',
    error: 'Fel',
    success: 'Succé',
    updated: 'Uppdaterade',
    added: 'La till',
    taskCompleted: 'Uppgift slutförd',
    created: 'Skapade',
    deleted: 'Tog bort',
    position: 'Position',
    direction: 'Riktning',
    anchor: 'Ankare',
    product: 'Produkt',
    vehicle: 'Fordon',
    map: 'Karta',
    settings: 'Inställningar',
    geofence: 'Geostaket',
    geofenceTop: 'Geostaket uppe',
    geofenceBottom: 'Geostaket nere',
    geofenceLeft: 'Geostaket vänster',
    geofenceRight: 'Geostaket höger',
    storageArea: 'Lagrings plats',
    inventory: 'Inventera',
    noAvailableOptions: 'Inga valbara alternativ',
    edit: 'Redigera',
    select: 'Välj',
    default: 'Standard',
    mirrored: 'Spegelvänt',
    add: 'Lägg till',
    top: 'Uppe',
    bottom: 'Nere',
    left: 'Vänster',
    right: 'Höger',
    longitude: 'Longitude',
    latitude: 'Latitude',
    shape: 'Figur',
    inside: 'Insida',
    outside: 'Utsida',
    logSorter: 'Sorteringslinje',
    confirm: 'Bekräfta',
    on: 'På',
    off: 'Av',
    circle: 'Cirkel',
    polygon: 'Polygon',
    size: 'Storlek',
    rotate: 'Rotera',
    freeform: 'Fri form',
    rectangle: 'Rektangel',
    empty: 'Töm',
    discard: 'Kasta bort',
    audit: 'Granska',
    export: 'Exportera',
    import: 'Importera',
    take: 'Ta',
    ok: 'Ok',
    from: 'Från',
    to: 'Till',
    move: 'Flytta',
    coil: 'Spole',
    swap: 'Växla',
    copy: 'Kopiera',
    pick: 'Plocka',
    unknown: 'Okänd',
    enabled: 'Aktiverad',
    note: 'Notering',
    groups: 'Grupper',
    productGroup: 'Produktgrupper',
    products: 'Produkter',
    numberOfLogs: 'Antal stockar',
    balance: 'Saldo',
    fifo: 'FIFO',
    volume: 'Volym',
    numberOfProductsInStorage: 'Antal produkter',
    storageAreas: 'Lagerplatser',
    showMore: 'Visa mer',
    preview: 'Förhandsvisning',
    selectColumns: 'Välj kolumner',
    locked: 'Låst',
    unlocked: 'Olåst',
    keybindings: 'Kortkommandon',
    productionOrder: 'Produktionsorder',
    resource: 'Resurs',
    priority: 'Prioritet',
    amount: 'Mängd',
    newVersion: 'Ny version av SILog',
    important: 'Viktigt',
    noData: 'Ingen data',
    total: 'Summa',
    calculate: 'Beräkna',
    sorted: 'Sorterat',
    unsorted: 'Osorterat',
    unit: 'Enhet',
  },
  application: {
    exit: 'Avsluta',
    exitSilog: 'Avsluta SILog',
    confirmExit: 'Är du säker på att du vill avsluta SILog?',
    openSettings: 'Öppna applikationsinställningar',
  },
  sideMenu: {
    home: 'Hem',
    map: 'Karta',
    vehicles: 'Fordon',
    products: 'Produkter',
    styles: 'Stilmallar',
    coils: 'Spolar',
    systemsettings: 'Systeminställningar',
    dashboards: 'Dashboards',
    productionOrders: 'Tillverkningsorder',
    reports: 'Rapporter',
  },
  vehicleType(type: VehicleType) {
    switch (type) {
      case VehicleType.Undefined:
        return 'Odefinerad';
      case VehicleType.Automatic:
        return 'Automatisk';
      case VehicleType.LogHandler:
        return 'Timmertruck';
      case VehicleType.Truck:
        return 'Truck';
      case VehicleType.CoilForklift:
        return 'Gaffeltruck med spolbom';
      case VehicleType.BulkHandler:
        return 'Bulk hanterare';
    }
  },
  vehicleSymbol(symbol: VehicleSymbol) {
    switch (symbol) {
      case VehicleSymbol.Undefined:
        return 'Pil (Standard)';
      case VehicleSymbol.LogStacker:
        return 'Höglyftare';
      case VehicleSymbol.WheelLoaderFrontGrapple:
        return 'Hjullastare - Frontgrip';
      case VehicleSymbol.WheelLoaderHighGrapple:
        return 'Hjullastare - Höglyftare';
      case VehicleSymbol.TimberCrane:
        return 'Timmerkran';
      case VehicleSymbol.CoilForklift:
        return 'Gaffeltruck med spolbom';
    }
  },
  productType(type: ProductType) {
    switch (type) {
      case ProductType.Log:
        return 'Stock';
      case ProductType.Undefined:
        return 'Odefinerad';
      case ProductType.Coil:
        return 'Spole';
      case ProductType.Bulk:
        return 'Bulk';
    }
  },
  products: {
    actions: {
      create: {
        title: 'Skapar produkt',
        error: 'Misslyckades att skapa produkt',
      },
      update: {
        title: 'Uppdaterar produkt',
        error: 'Misslyckades att uppdatera produkt',
      },
      delete: {
        title: 'Tar bort produkt',
        error: 'Misslyckades att ta bort produkt',
        statusCodes: {
          409: 'Produkten används fortfarande i en lagerplats',
        },
      },
      fetch: {
        title: 'Hämtar produkt',
        error: 'Misslyckades att hämta produkt',
      },
      fetchMany: {
        title: 'Hämtar produkter',
        error: 'Misslyckades att hämta produkter',
      },
      editLogYardProducts(number: number) {
        return `Redigera ${number} timmer produkter`;
      },
      editCoilProducts(number: number) {
        return `Redigera ${number} spol produkter`;
      },
    },

    deleteDialog: {
      header: (name: string) => `Ta bort produkt ${name}`,
      description:
        'Är du säkert på att du vill ta bort den här produkten? Denna åtgärd kan inte ångras.',
      deleteButton: 'Ja, ta bort produkten',
    },

    products: 'Produkter',
    addNewProduct: 'Lägg till ny produkt',
    editProduct: 'Redigera produkt',
    editMultipleProducts(number: number) {
      return `Redigera ${number} produkter`;
    },
    fetchDataError: 'Det gick inte hämta produkt data',
    addNewProductError: 'Kunde inte skapa produkten',
    editProductError: 'Kunde inte uppdatera produkten',
    deleteProductError: 'Kunde inte ta bort produkten',
    noProductsFound: 'Inga produkter hittades.',
    loadingProductData: 'Laddar produkt data..',
    logsPerSquareMeter: 'stockar/m²',
    notFound: 'Kunde inte hitta produkt',
    couldNotFetchProduct: 'Kunde inte hämta produkt!',
    logData: {
      logData: 'Stock data',
      logsPerSquareMeter: 'Stockar per m²',
      minDiameter: 'Min diameter',
      maxDiameter: 'Max diameter',
      maxLength: 'Max längd',
      minLength: 'Min längd',
      averageCylinderVolume: 'Genomsnitt cylinder volym',
      averagePhysicalVolume: 'Genomsnitt fysisk volym',
      isSorted: 'Är sorterad',
      species: 'Träslag',
      logSorterName: 'Timmersorterare namn',
      sawLineName: 'Såglinje namn',
      logsPerSquareMeterDescription:
        'Används för att estimera hur många stockar ett fordon kan ta från en lagerplats',
    },
    coilData: {
      title: 'Spole',
      diameter: 'Diameter',
      depth: 'Djup',
    },
    bulkData: {
      title: 'Bulk',
      unit: 'Enhet',
      density: 'Densitet',
    },
  },
  productGroups: {
    actions: {
      create: {
        title: 'Skapar produktgrupp',
        error: 'Misslyckades att skapa produktgrupp',
      },
      update: {
        title: 'Uppdaterar produktgrupp',
        error: 'Misslyckades att uppdatera produktgrupp',
      },
      delete: {
        title: 'Tar bort produktgrupp',
        error: 'Misslyckades att ta bort produktgrupp',
      },
      fetch: {
        title: 'Hämtar produktgrupp',
        error: 'Misslyckades att hämta produktgrupp',
      },
      fetchMany: {
        title: 'Hämtar produktgrupper',
        error: 'Misslyckades att hämta produktgrupper',
      },
    },
    listPage: {
      title: 'Produktgrupper',
      empty: 'Inga produktgrupper',
      loading: 'Laddar produktgrupper...',
      addNew: 'Lägg till produktgrupp',
      edit: 'Redigera grupp',
    },
    deleteDialog: {
      header: (name: string) => `Ta bort produktgrupp ${name}`,
      description:
        'Är du säkert på att du vill ta bort den här produktgruppen? Denna åtgärd kan inte ångras.',
      deleteButton: 'Ja, ta bort produktgruppen',
    },
  },
  vehicles: {
    actions: {
      create: {
        title: 'Skapar fordon',
        error: 'Misslyckades att skapa fordon',
      },
      update: {
        title: 'Uppdaterar fordon',
        error: 'Misslyckades att uppdatera fordon',
      },
      delete: {
        title: 'Tar bort fordon',
        error: 'Misslyckades att ta bort fordon',
      },
      fetch: {
        title: 'Hämtar fordon',
        error: 'Misslyckades att hämta fordon',
      },
      fetchMany: {
        title: 'Hämtar flera fordon',
        error: 'Misslyckades att hämta flera fordon',
      },
      startReservation: {
        title: 'Reserverar fordon',
        error: 'Misslyckades att reservera fordon',
      },
      endReservation: {
        title: 'Avslutar fordonsreservation',
        error: 'Misslyckades att avsluta fordonsreservation',
      },
      updateKeybinds: {
        title: 'Uppdaterar fordonets kortkommandon',
        error: 'Misslyckades att uppdaterar fordonets kortkommandon',
      },
    },
    vehicles: 'Fordon',
    noVehiclesFound: 'Inga fordon hittades.',
    loadingVehicleData: 'Laddar fordons data..',
    addNewVehicle: 'Lägg till nytt fordon',
    editVehicle: 'Redigera fordon',
    confirmDeleteMsg:
      'Är du säkert på att du vill ta bort det här fordonet? Denna åtgärd kan inte ångras.',
    deleteBtnTxt: 'Ja, ta bort fordonet',
    fetchDataError: 'Det gick inte hämta fordons data',
    editVehicleSuccess: 'Succé: redigerade',
    addNewVehicleSuccess: 'Succé: lade till',
    deleteVehicleSuccess: 'Succé: tog bort',
    deleteVehicleError: 'Kunde inte ta bort fordonet:',
    nameMustBeUnique: 'Namnet måste vara unikt',
    symbol: 'Symbol',
    symbolPreview: 'Förhandsgranskning',
    capacityDescription:
      'Används i kombination med produktens "stockar/m²" för att estimera hur många stockar som fordonet kan ta från en lagerplats.',
    deleteVehicle(name: string) {
      return `Ta bort fordon ${name}`;
    },
    inUseDialog: {
      header: 'Fordonet används!',
    },
    reservationEnded: 'En annan användare tog över ditt fordon!',
    reservationEndedDescription: (userId: string) => `Ditt fordon togs av ${userId.split('|')[0]}`,
    automaticLogYardMoves: 'Flytta stockar automatiskt',
    automaticLogYardMovesLogSorterBinFull: 'När facket är fullt',
    automaticLogYardMovesLogSorterBinChangeProduct: 'När facket byter sortiment',
  },
  addLogSorterDialog: {
    addLogSorter: 'Lägg till timmersortering',
    editLogSorter: 'Redigera timmersortering',
    name: 'Namn',
    bin: 'Fack',
    bins: 'Fack',
    identifiers: 'Id',
    logSorter: 'Timmersortering',
    selectLogSorter: 'Välj timmersortering',
    startPositionX: 'Start position X',
    startPositionY: 'Start position Y',
    columns: 'Kolumner',
    numberOfBins: 'Antal fack',
    numberSequence: 'Nummer följd',
    rotation: 'Rotation',
    direction: 'Riktning',
    binWidth: 'Fack bredd',
    binLength: 'Fack längd',
    distanceX: 'Avstånd emellan X-led',
    distanceY: 'Avstånd emellan Y-led',
    geoFenceOffsetTop: 'Geofence toppen',
    geoFenceOffsetBottom: 'Geofence botten',
    geoFenceOffsetLeft: 'Geofence vänster',
    geoFenceOffsetRight: 'Geofence höger',
    geoFenceOffsetInside: 'Geofence insida',
    geoFenceOffsetOutside: 'Geofence utsida',
    addLogWhenSorted: 'Lägg till stock vid sortering',
    intake: 'Intag',
    intakes: 'Intag',
    addNewIntake: 'Lägg till nytt intag',
    deleteIntake: 'Ta bort intag',
    savingStorageAreas: 'Sparar förvarings platser',
    savingShapes: 'Sparar kart positioner',
    savingLogSorter: 'Sparar timmersortering',
    success: 'Klart!',
    savingBins: 'Sparar fack',
    savingIntakes: 'Sparar intag',
    one: '1',
    twoLeft: '2L',
    twoRight: '2R',
  },
  sites: {
    actions: {
      fetchMany: {
        title: 'Hämtar arbetsplatser',
        error: 'Misslyckades att hämta arbetsplatser',
      },
    },
    notFound: 'Kunde inte hitta site',
  },
  dialogs: {
    storageArea: {
      addStorageArea: 'Skapa lagringsplats',
      editStorageArea: 'Redigera lagringsplats',
      storageArea: 'Lagrings plats',
      automaticProductChange: 'Automatisk produkt byte',
      allowMultipleProducts: 'Tillåt flera produkter',
      allowedProducts: 'Tillåtna produkter',
      selectAllowedProducts: 'Välj tillåtna produkter',
      selectStorageAreaType: 'Välj lagringsplats typ',
      selectStorageAreaStatus: 'Välj lagringsplats status',
      confirmDeleteHeader: 'Ta bort lagringsplats',
      confirmDeleteMessage: (name: string) =>
        `Är du säker på att du vill ta bort lagringsplatsen: ${name}?`,
      deleteError: 'Kunde inte ta bort lagringsplatsen',
      updateError: 'Kunde inte uppdatera lagringsplatsen',
      createError: 'Kunde inte skapa lagringsplatsen',
      editShape: 'Redigera figur',
      sideArea: 'Yta från sidan',
      sideAreaDescription: 'Används för att estimera hur många stockar som ryms inom lagerplatsen',
    },
    storageAreaSelect: {
      title: 'Lagringsplats info',
    },
    inventory: {
      title: 'Inventera',
      selectProduct: 'Välj produkt',
      currentAmount: 'Nuvarande mängd:',
      setNewAmount: 'Sätt ny mängd',
      storageAreaNotFound: 'Kunde inte hitta lagerplats',
      confirmHeader: (name: string) => `Inventera ${name}`,
      confirmMessage: (name: string, product: string) =>
        `Är du säker på att du vill lägga till ${product} i ${name}? Tillåt flera produkter är inte aktiverat.`,
    },
    confirmEmpty: {
      message:
        'Det finns fortfarande innehåll i lagringsplatsen. Är du säker på att du vill tömma lagringsplatsen? Återstående innehåll kommer att tas bort.',
      title: (name: string) => `Töm lagringsplatsen ${name}`,
      willBeDeleted: 'kommer att tas bort',
    },
    confirmAllowMultipleProducts: {
      message: (storageAreaName: string, product: string) =>
        `Tillåt flera produkter är inaktiverat. Är du säker på att du vill placera "${product}" i ${storageAreaName}`,
      title: 'Varning',
    },
    confirmPlaceNotAllowedProductInLogYard: {
      message: (storageAreaName: string, product: string) =>
        `Produkten "${product}" är inte tillåten i ${storageAreaName}. Är du säker på att du vill placera den här?`,
      title: 'Warning',
    },
    moveLogYardContent: {
      pickTab: 'Hämta från',
      pickButton: 'Hämta',
      pickAll: 'Töm',
      placeTab: 'Lämna till',
      placeButton: 'Lämna',
    },
    selectVehicle: {
      title: 'Välj fordon',
      message: 'Vänligen välj vilket fordon du använder',
    },
    vehicleInventory: {
      vehicleInventory: 'Fordons inventarie',
      vehicleLoad: 'Fordonets nuvarande last',
      leaveTitle: 'Lämna i',
      leaveButton: 'Lämna',
      moving: 'Flyttar',
    },
    search: {
      searchForProduct: 'Sök efter en produkt',
      selectAProduct: 'Välj en produkt',
    },
    mapView: {
      title: 'Lägg till en ny kart vy',
      enterName: 'Skriv in ett namn',
    },
    selectLogSorter: {
      title: 'Välj sorteringslinje',
    },
    productionOrder: {
      editTitle: 'Välj tillverkningsorder',
      selectProductionOrder: 'Välj tillverkningsorder',
    },
  },
  error: {
    required: 'krävs.',
    invalidValue: 'Ogiltigt värde.',
    nameAlreadyExist: 'Namnet finns redan.',
    nameIsRequired: 'Ett namn måste anges.',
    maxLength: (limit: number) => `Maxlängd ${limit}`,
    forbidden: 'Förbjuden',
    notFound: 'Kunde inte hitta',
    conflict: 'Konflikt',
    network: 'Kunde inte nå servern',
    invalidDensity: 'Densiteten måste vara minst 0,1'
  },
  enums: {
    storageAreaStatus(status) {
      switch (status) {
        case StorageAreaStatus.Empty:
          return 'Tom';
        case StorageAreaStatus.InUse:
          return 'I användning';
        case StorageAreaStatus.Full:
          return 'Full';
        case StorageAreaStatus.Undefined:
          return 'Odefinerad';
      }
    },
    storageAreaType(type) {
      switch (type) {
        case StorageAreaType.Undefined:
          return 'Odefinerad';
        case StorageAreaType.Producer:
          return 'Producent';
        case StorageAreaType.Consumer:
          return 'Konsument';
        case StorageAreaType.Vehicle:
          return 'Fordon';
        case StorageAreaType.LogSorterIntake:
          return 'Timmersortering intag';
        case StorageAreaType.SawLineIntake:
          return 'Såglinje intag';
        case StorageAreaType.LogSorterBin:
          return 'Timmersortering fack';
        case StorageAreaType.SortedPile:
          return 'Sorterad välta';
        case StorageAreaType.UnsortedPile:
          return 'Osorterad Välta';
        case StorageAreaType.Truck:
          return 'Lastbil';
        case StorageAreaType.Train:
          return 'Tåg';
        case StorageAreaType.CoilStack:
          return 'Staplade spolar';
        case StorageAreaType.Bulk:
          return 'Bulk';
      }
    },
    shapeMode(type: ShapeMode): string {
      switch (type) {
        case ShapeMode.Rectangle:
          return 'Rektangel';
        case ShapeMode.Circle:
          return 'Cirkel';
        case ShapeMode.Polygon:
          return 'Polygon';
      }
    },
    logSorterNumberSequence(value: LogSorterNumberSequence): string {
      switch (value) {
        case LogSorterNumberSequence.Sequential:
          return 'Sekventiell';
        case LogSorterNumberSequence.SequentialMirrored:
          return 'Sekventiell spegelvänd';
        case LogSorterNumberSequence.Alternating:
          return 'Alternerande';
        case LogSorterNumberSequence.AlternatingMirrored:
          return 'Alternerande spegelvänd';
      }
    },
    logSorterBinLayout(value: LogSorterColumnPlacement): string {
      switch (value) {
        case LogSorterColumnPlacement.Left:
          return 'Vänster';
        case LogSorterColumnPlacement.Right:
          return 'Höger';
      }
    },
    anchor(value: Anchor) {
      switch (value) {
        case Anchor.Center:
          return 'I mitten';
        case Anchor.BottomCenter:
          return 'Nere i mitten';
        case Anchor.BottomLeft:
          return 'Nere till vänster';
        case Anchor.BottomRight:
          return 'Nere till höger';
        case Anchor.TopCenter:
          return 'Uppe i mitten';
        case Anchor.TopLeft:
          return 'Uppe till vänster';
        case Anchor.TopRight:
          return 'Uppe till höger';
      }
    },
    auditTrailAction(action: AuditTrailAction): string {
      switch (action) {
        case AuditTrailAction.InventoryApiExecute:
          return 'Inventera';
        case AuditTrailAction.InventoryApiUndo:
          return 'Ångra inventering';
        case AuditTrailAction.LogSorterApiCreate:
          return 'Skapa sorteringslinje';
        case AuditTrailAction.LogSorterApiDelete:
          return 'Ta bort sorteringslinje';
        case AuditTrailAction.LogSorterApiUpdate:
          return 'Redigera sorteringslinje';
        case AuditTrailAction.MapStyleApiCreate:
          return 'Skapa stilmall för karta';
        case AuditTrailAction.MapStyleApiDelete:
          return 'Ta bort stilmall för karta';
        case AuditTrailAction.MapStyleApiUpdate:
          return 'Redigera stilmall för karta';
        case AuditTrailAction.MoveApiUndo:
          return 'Ångra flytt';
        case AuditTrailAction.MoveApi:
          return 'Flytta';
        case AuditTrailAction.ProductApiCreate:
          return 'Skapa produkt';
        case AuditTrailAction.ProductApiDelete:
          return 'Ta bort produkt';
        case AuditTrailAction.ProductApiUpdate:
          return 'Redigera produkt';
        case AuditTrailAction.ShapeApiCreate:
          return 'Skapa figur';
        case AuditTrailAction.ShapeApiDelete:
          return 'Ta bort figur';
        case AuditTrailAction.ShapeApiUpdate:
          return 'Redigera figur';
        case AuditTrailAction.ShapeApiUndoDelete:
          return 'Ångra ta bort figur';
        case AuditTrailAction.StorageAreaApiCreate:
          return 'Skapa lagerplats';
        case AuditTrailAction.StorageAreaApiDelete:
          return 'Ta bort lagerplats';
        case AuditTrailAction.StorageAreaApiUndoDelete:
          return 'Ångra borttag av lagerplats';
        case AuditTrailAction.StorageAreaApiUpdate:
          return 'Redigera lagerplats';
        case AuditTrailAction.SyncMove:
          return 'Flytta';
        case AuditTrailAction.VehicleApiCreate:
          return 'Skapa fordon';
        case AuditTrailAction.VehicleApiDelete:
          return 'Ta bort fordon';
        case AuditTrailAction.VehicleApiUpdate:
          return 'Redigera fordon';
        case AuditTrailAction.Undefined:
          return 'Inte definierad';
        case AuditTrailAction.SyncInventory:
          return 'Synka inventering';
        case AuditTrailAction.SyncProduct:
          return 'Synka produkt';
        case AuditTrailAction.SyncShape:
          return 'Synka figur';
        case AuditTrailAction.SyncStorageArea:
          return 'Synka lagerplats';
        case AuditTrailAction.SyncUndoInventory:
          return 'Synka ångra inventering';
        case AuditTrailAction.SyncUndoMove:
          return 'Synka ångra flytt';
        case AuditTrailAction.SyncUndoStorageArea:
          return 'Synka ångra lagerplats';
        case AuditTrailAction.CoilApiCreate:
          return 'Create coil';
        case AuditTrailAction.CoilApiUpdate:
          return 'Update coil';
        case AuditTrailAction.CoilApiDelete:
          return 'Delete coil';
        case AuditTrailAction.FeatureFlagApi:
          return 'Redigerar feature flags';
        case AuditTrailAction.SettingUpdate:
          return 'Redigerar inställningar';
        case AuditTrailAction.SyncFeatureFlag:
          return 'Synka feature flags';
        case AuditTrailAction.SyncLogSorter:
          return 'Synka timmersortering';
        case AuditTrailAction.SyncMapStyle:
          return 'Synka stilar mallar';
        case AuditTrailAction.SyncSetting:
          return 'Synka inställningar';
        case AuditTrailAction.SyncVehicle:
          return 'Synka fordon';
        case AuditTrailAction.ProductGroupApiCreate:
          return 'Skapa produktgrupp';
        case AuditTrailAction.ProductGroupApiUpdate:
          return 'Redigera produktgrupp';
        case AuditTrailAction.ProductGroupApiDelete:
          return 'Ta bort produktgrupp';
        case AuditTrailAction.ProductGroupSync:
          return 'Synka produktgrupp';
        case AuditTrailAction.DashboardCreate:
          return 'Skapa dashboard';
        case AuditTrailAction.DashboardUpdate:
          return 'Redigera dashboard';
        case AuditTrailAction.DashboardDelete:
          return 'Ta bort dashboard';
        case AuditTrailAction.MoveTimberFromLogSorterBins:
          return 'Flytta stockar från fack';
        case AuditTrailAction.ProductionOrderCreate:
          return 'Skapa tillverkningsorder';
        case AuditTrailAction.ProductionOrderUpdate:
          return 'Redigera tillverkningsorder';
        case AuditTrailAction.ProductionOrderDelete:
          return 'Ta bort tillverkningsorder';
        case AuditTrailAction.ProductionOrderSync:
          return 'Synka tillverkningsorder';
        case AuditTrailAction.ProductionOrderChange:
          return 'Ändra tillverkningsorder';
      }
    },
    dashboardComponentType(type: DashboardComponentType) {
      switch (type) {
        case DashboardComponentType.StorageAreaTable:
          return 'Lagerplatstabell';
        case DashboardComponentType.LatestReleaseNotes:
          return 'Senaste release notes';
        case DashboardComponentType.ProductGroupDiagram:
          return 'Produktgruppdiagram';
        case DashboardComponentType.ProductGroupTable:
          return 'Produktgruppstabell';
        case DashboardComponentType.ProductTable:
          return 'Produkttabell';
        case DashboardComponentType.AsljungapallenLagersaldo:
          return 'Åsljungapallen lagersaldo';
        default:
          return 'Okänd Komponenttyp';
      }
    },
    keyboardAction(action: KeyboardActions): string {
      switch (action) {
        case KeyboardActions.LeaveInLogYard:
          return 'Lämna';
        case KeyboardActions.PickFromLogYard:
          return 'Hämta';
      }
    },
    productionOrderState(state: ProductionOrderState): string {
      switch (state) {
        case ProductionOrderState.Deleted:
          return 'Borttagen';
        case ProductionOrderState.Planned:
          return 'Planerad';
        case ProductionOrderState.Released:
          return 'Frisläppt';
        case ProductionOrderState.Reserved:
          return 'Reserverad';
        case ProductionOrderState.Preparing:
          return 'Förbereder';
        case ProductionOrderState.Prepared:
          return 'Förberedd';
        case ProductionOrderState.Delivering:
          return 'Levererar';
        case ProductionOrderState.Completed:
          return 'Avslutad';
        case ProductionOrderState.Registered:
          return 'Registrerad';
        case ProductionOrderState.PreparingPaused:
          return 'Förberedelse pausad';
        case ProductionOrderState.DeliveringPaused:
          return 'Leverans pausad';
      }

      return 'Okänd';
    },
  },
  reports: {
    exportFormat: {
      pdf: 'PDF',
      excel: 'Excel',
    },
    reportName(report: ReportType): string {
      switch (report) {
        case ReportType.LogYardStatus:
          return 'Statusrapport';
        case ReportType.LogYardPeriod:
          return 'Periodrapport';
        default:
          return 'Okänd rapport';
      }
    },
  },
  storageAreas: {
    actions: {
      create: {
        title: 'Skapa lagerplats',
        pending: 'Skapar lagerplats',
        error: 'Misslyckades att skapa lagerplats',
      },
      fetch: {
        title: 'Hämtar lagerplats',
        error: 'Misslyckades att hämta lagerplats',
      },
      fetchMany: {
        title: 'Hämtar lagerplatser',
        error: 'Misslyckades att hämta lagerplatser',
      },
      inventory: {
        title: 'Inventerar',
        error: 'Misslyckades att inventera',
      },
      undoInventory: {
        title: 'Ångra inventering',
        error: 'Misslyckades att ångra inventering',
      },
      delete: {
        title: 'Tar bort lagerplats',
        error: 'Misslyckades att ta bort lagerplats',
      },
      update: {
        title: 'Uppdaterar lagerplats',
        error: 'Misslyckades att uppdatera lagerplats',
      },
      undoDelete: {
        title: 'Ångra ta bort lagerplats',
        error: 'Misslyckades att ångra ta bort lagerplats',
      },
    },
    notFound: 'Kunde inte hitta lagerplats',
    copy: 'Kopia',
    storageAreaTable: {
      firstMoveTo: 'Första flytten',
      daysSinceFirstMove: 'Dagar sedan första flytt',
    },
  },
  productionOrders: {
    actions: {
      fetchMany: {
        title: 'Hämtar tillverkningsorder',
        error: 'Misslyckades att hämta tillverkningsorder',
      },
      fetch: {
        title: 'Hämtar tillverkningsorder',
        error: 'Misslyckades att hämta tillverkningsorder',
      },
      create: {
        title: 'Skapar tillverkningsorder',
        error: 'Misslyckades att skapa tillverkningsorder',
      },
      update: {
        title: 'Uppdaterar tillverkningsorder',
        error: 'Misslyckades att uppdatera tillverkningsorder',
      },
      delete: {
        title: 'Tar bort tillverkningsorder',
        error: 'Misslyckades att ta bort tillverkningsorder',
        statusCodes: {
          409: 'Misslyckades att ta bort tillverkningsorder',
        },
      },
      change: {
        title: 'Ändrar tillverkningsorder',
        error: 'Misslyckades att ändra tillverkningsorder',
      },
    },
    listPage: {
      title: 'Tillverkningsorder',
      noFound: 'Inga tillverkningsorder hittades',
      loading: 'Laddar tillverkningsorder...',
    },
    deleteDialog: {
      header(name: string) {
        return `Ta bort tillverkningsorder ${name}`;
      },
      description:
        'Är du säker på att du vill ta bort denna tillverkningsorder? Denna åtgärd kan inte ångras.',
      deleteButton: 'Ja, ta bort tillverkningsorder',
    },
    editPage: {
      editTitle: 'Redigera tillverkningsorder',
      createTitle: 'Skapa tillverkningsorder',
      productionInstruction: 'Produktionsinstruktioner',
      vehicleInstruction: 'Fordonsinstruktioner',
      state: 'Status',
      recipeInstruction: 'Instruktioner',
      recipeMachineInstruction: 'Maskin instruktioner',
      consumeEntireStock: 'Förbruka hela lagret',
      noProducts: 'Inga produkter',
      inProductsTitle: 'In produkter',
      outProductsTitle: 'Ut produkter',
      editRecipeTitle: 'Redigera recept',
      addRecipeTitle: 'Lägg till recept',
      addRecipeButton: 'Lägg till recept',
      recipesTitle: 'Recept',
    },
    plannedStart: 'Planerad start',
  },
  shapes: {
    name: (id: string) => `Figur #${id}`,
    multipleNames: (count: number) => `Redigerar ${count} figurer`,
    actions: {
      fetchMany: {
        title: 'Hämtar figurer',
        error: 'Misslyckades att hämta figurer',
      },
      fetch: {
        title: 'Hämtar figur',
        error: 'Misslyckades att hämta figur',
      },
      update: {
        title: 'Redigerar figur',
        error: 'Misslyckades att redigera figur',
      },
      create: {
        title: 'Skapa figur',
        pending: 'Skapar figur',
        error: 'Misslyckades att skapa figur',
      },
      delete: {
        title: 'Tar bor figur',
        error: 'Misslyckades att ta bort figur',
      },
      query: {
        title: 'Söker efter figurer',
        error: 'Misslyckades att söka efter figurer',
      },
      undoDelete: {
        title: 'Ångra ta bort figur',
        error: 'Misslyckades att ångra ta bort figur',
      },
    },
    geofenceFieldSet: 'Geofence',
  },
  settings: {
    actions: {
      read: {
        title: 'Read settings',
        error: 'Failed to read settings',
      },
      save: {
        title: 'Spara inställningar',
        error: 'Misslyckades att spara inställningar',
      },
    },
    general: {
      title: 'Generellt',
      language: 'Språk',
      dark: 'Mörk',
      light: 'Ljus',
      colorMode: 'Färg läge',
      scale: 'Storlek på användargränssnitt',
    },
    map: {
      title: 'Karta',
      style: 'Stilmall',
      coordinate: 'Koordinater',
      centerMapOnVehicle: 'Centrera kartan på fordonet',
    },
    keybinds: {
      title: 'Kortkommandon',
    },
    about: {
      title: 'Om',
    },
    vehicle: {
      title: 'Fordon',
      activeVehicle: 'Aktivt fordon',
      speedLimit: 'Hastighetsbegränsning 5 km/h för att trigga lagringsplats',
    },
  },
  featureFlags: {
    actions: {
      read: {
        title: 'Läser in funktioner',
        error: 'Misslyckades att läsa in funktioner',
      },
      refresh: {
        title: 'Uppdaterar funktioner',
        error: 'Misslyckades att uppdatera funktioner',
      },
      toggle: {
        title: 'Aktivera/deaktivera funktioner',
        error: 'Misslyckades att aktivera/deaktivera funktioner',
      },
    },
    refresh: {
      success: 'Funktioner har uppdaterats',
    },
    toggle: {
      success: 'Funktion flagga har ändras',
    },
    component: {
      title: 'Funktioner',
      description: 'Aktivera eller inaktivera funktioner i applikationen',
      refreshButton: 'Uppdatera funktioner',
    },
  },
  auth: {
    loggedOut: {
      title: 'Du är utloggad!',
      description: 'Du måste logga in för att fortsätta arbeta.',
    },
    unauthorized: {
      title: 'Din användare har inte rättigheter att använda den här appen',
      description: 'Du måste prata med systemets administrator för att få rättigheter.',
    },
  },
  map: {
    contextmenu: {
      create: {
        title: 'Skapa',
        shape: 'Figur',
        storageArea: 'Lagerplats',
        logSorter: 'Sorteringslinje',
        intake: 'Intag',
      },
      edit: 'Redigera',
      delete: 'Ta bort',
      audit: 'Granska',
      copy: 'Kopiera',
      move: 'Flytta',
    },
    actions: {
      saveMapMode: {
        title: 'Spara kartläge',
        error: 'Misslyckades att spara kartläge',
      },
    },
  },
  logSorter: {
    actions: {
      query: {
        title: 'Söker sorteringslinje',
        error: 'Misslyckades att söka sorteringslinje',
      },
      update: {
        title: 'Uppdaterar sorteringslinje',
        error: 'Misslyckades att updatera sorteringslinje',
      },
      create: {
        title: 'Skapar sorteringslinje',
        error: 'Misslyckades att skapa sorteringslinje',
      },
      delete: {
        title: 'Tar bort sorteringslinje',
        error: 'Misslyckades att ta bort sorteringslinje',
      },
      fetch: {
        title: 'Hämtar sorteringslinje',
        error: 'Misslyckades att hämta sorteringslinje',
      },
    },
    drawer: {
      createTitle: 'Skapa sorteringslinje',
      binName: (binNumber: number) => `Fack ${binNumber}`,
      logSorterTab: 'Sorteringslinje',
      binsTab: 'Fack',
    },
    deleteConfirm: {
      title: `Ta bort sorteringslinje`,
      message: (name: string) => `Är du säker att du vill ta bort sorteringslinje ${name}?`,
    },
    panel: {
      identifier: 'Identifierare',
      numberSequence: 'Nummer följd',
      numberOfBinsCol1: 'Antal fack i kolumn ett',
      numberOfBinsCol2: 'Antal fack i kolumn två',
      binLayout: 'Kolumn två till',
      binWidth: 'Fack bredd',
      binLength: 'Fack längd',
      columnGap: 'Kolumnmellanrum',
      rowGap: 'Radmellanrum',
      intakes: 'Intag',
      offsetY: 'Offset Y',
      offsetX: 'Offset X',
    },
  },
  mapStyles: {
    actions: {
      create: {
        title: 'Skapar stilmall',
        error: 'Misslyckades att skapa stilmall',
      },
      update: {
        title: 'Uppdaterar stilmall',
        error: 'Misslyckades att uppdatera stilmall',
      },
      delete: {
        title: 'Tar bort stilmall',
        error: 'Misslyckades att ta bort stilmall',
      },
      fetch: {
        title: 'Hämtar stilmall',
        error: 'Misslyckades att hämta stilmall',
      },
      fetchMany: {
        title: 'Hämtar stilmallar',
        error: 'Misslyckades att hämta stilmallar',
      },
    },

    deleteDialog: {
      header: (name: string) => `Ta bort stilmall ${name}`,
      description:
        'Är du säkert på att du vill ta bort den här stilmallen? Denna åtgärd kan inte ångras.',
      deleteButton: 'Ja, ta bort stilmallen',
    },

    editDialog: {
      create: {
        title: 'Skapa ny stilmall',
      },
      edit: {
        title: 'Redigera stilmall',
      },
      tip: 'Ni kan trycka ctrl+space för att öppna information om tillgängliga funktioner',
      documentation: 'För mer information om formlerna, se ',
      documentationLinkText: 'dokumentation för stillmallar',
      shapeSet: 'Figur',
      vehicleSet: 'Fordon',
      labelFormula: 'Text',
      colorFormula: 'Färg',
      outlineColorFormula: 'Konturfärg',
      outlineSizeFormula: 'Konturstorlek',
      fontSizeFormula: 'Textstorlek',
      messageIconFormula: 'Visa meddelandeikon',
      colorPicker: 'Färgväljare (Används för att välja och visa färg koder)',
    },

    page: {
      title: 'Stilmallar för karta',
      notFound: 'Kunde inte hitta stilmallar',
      loading: 'Laddar stilmallar',
      copiedToClipboard: 'Kopierad till klippbordet',
      noStyleInClipboard: 'Fanns ingen stilmall i klippbordet',
      noStyleInClipboardDescription:
        'Du måste exportera en stilmall till klippbordet innan du kan importerar det.',
      importFromClipboardTooltip: 'Importera från klippbord',
    },
  },
  systemSettings: {
    page: {
      title: 'Systeminställningar',
      settingsSaved: 'Inställningar sparade',
      storagearea: {
        title: 'Lagerplatser',
        nameFormat: 'Namnformat',
        nameFormatRequired: 'Namnformat krävs',
        nameFormatMissingSequence: 'Namnformatet måste innehålla platshållaren {#} för sekvens',
        logYardLength: 'Timmerplan längd',
        logYardWidth: 'Timmerplan bredd',
        logYardSideArea: 'Timmerplan sidoyta',
        logYard: 'Timmerplan',
      },
      featureflags: {
        title: 'Funktioner',
      },
      reports: {
        title: 'Rapporter',
      },
      map: {
        minZoom: 'Kartans lägsta zoomnivå (0-24)',
      },
      dashboards: {
        title: 'Dashboardskomponenter',
      },
    },
  },
  actions: {
    fetchVersion: {
      title: 'Hämtar applikationsversion',
      error: 'Misslyckades att hämta applikationsversion',
    },
  },
  undo: {
    undoBtn: 'Ångra',
    pickTitle: 'Ångra hämta',
    getUndoPickDescription(
      oldStorageAreaCount: number,
      oldVehicleCount: number,
      storageAreaName: string,
    ): string {
      return `Bekräfta för att ta bort ${oldVehicleCount} stockar från fordonet och återställa lagringsplatsen ${storageAreaName} till antal stockar: ${oldStorageAreaCount}`;
    },
    placeTitle: 'Ångra lämna',
    getUndoPlaceDescription(numberOfLogs: number, storageAreaName: string): string {
      return `Bekräfta för att återställa ${numberOfLogs} stockar till ditt fordon och ta bort ${numberOfLogs} stockar från lagringsplatsen ${storageAreaName}`;
    },
    inventoryTitle: 'Ångra inventering',
    getUndoInventoryDescription(
      productName: string,
      numberOfLogs: number,
      storageAreaName: string,
    ): string {
      return `Bekräfta för att ångra senaste inventeringen för lagringsplatsen ${storageAreaName} och återställ antal ${productName} till ${numberOfLogs}`;
    },
    getUndoBulkInventoryDescription(
      productname: string,
      value: number,
      unit: Unit,
      storageAreaName: string,
    ): string {
      return `Bekräfta för att ångra senaste inventeringen för lagringsplatsen ${storageAreaName} och återställ mängden ${productname} till ${value}`;
    },
    createStorageAreaTitle: 'Ångra skapa lagringsplats',
    getUndoCreateStorageAreaDescription(storageAreaName: string): string {
      return `Bekräfta för att ångra skapa lagringsplatsen ${storageAreaName}`;
    },
    deleteStorageAreaTitle: 'Ångra ta bort lagringsplatsen',
    getUndoDeleteStorageAreaDescription(storageAreaName: string): string {
      return `Bekräfta för att ångra ta bort lagringsplatsen ${storageAreaName}`;
    },
    editStorageAreaTitle: 'Ångra redigera lagringsplats',
    getUndoEditStorageAreaDescription(storageAreaName: string): string {
      return `Bekräfta för att ångra redigera lagringsplatsen ${storageAreaName}`;
    },
    createShape: {
      title: 'Ångra skapa figur',
      description: `Bekräfta för att ångra skapa figur`,
    },
    updateShape: {
      title: 'Ångra redigera figur',
      description: 'Bekräfta för att ångra redigera figur',
    },
    deleteShape: {
      title: 'Ångra ta bort figur',
      description: 'Bekräfta för att ångra ta bort figur',
    },
  },
  quickMenu: {
    addStorageArea: 'Skapa lagringsplats',
    editStorageArea: 'Redigera lagringsplats',
  },
  moves: {
    actions: {
      moveTimberFromLogSorterBins: {
        title: 'Flytta stockar från fack',
        error: 'Misslyckades att flytta stockar från fack',
      },
      pick: {
        title: 'Hämtar från lagerplats',
        error: 'Misslyckades att hämta från lagerplats',
      },
      place: {
        title: 'Lämnar i lagerplats',
        error: 'Misslyckades att lämna i lagerplats',
      },
      pickAndPlace: {
        title: 'Hämta och lämna',
        error: 'Misslyckades att hämta och lämna',
      },
    },
    failedDialog: {
      title: 'Flytt misslyckades!',
      description: (fromStorageArea: string, toStorageArea: string) =>
        `Flytta från ${fromStorageArea} till ${toStorageArea}`,
    },
    moveTimberFromLogSorterBins: 'Flytta stockar från fack',
  },
  home: {
    welcome: 'Välkommen till',
    description: 'Nästa generationens logistisk system',
    username: 'Användarnamn',
    enterUsername: 'Ange ditt användarnamn för att logga in',
    rememberUsername: 'Kom ihåg användarnamn',
    signInButton: 'Logga in',
  },
  unsavedChanges: {
    title: 'Du har osparade ändringar!',
    description: 'Vad vill du göra med dom?',
  },
  audit: {
    dialog: {
      title: (name: string) => `Ändringar av ${name}`,
    },
    actions: {
      fetchShape: {
        title: 'Hämtar granskning av figur',
        error: 'Misslyckades att hämta granskning av figur',
      },
      fetchVehicle: {
        title: 'Hämtar granskning av fordon',
        error: 'Misslyckades att hämta granskning av fordon',
      },
      fetchProduct: {
        title: 'Hämtar granskning av produkt',
        error: 'Misslyckades att hämta granskning av produkt',
      },
      fetchProductGroup: {
        title: 'Hämtar granskning av produktgrupp',
        error: 'Misslyckades att hämta granskning av produktgrupp',
      },
      fetchDashboard: {
        title: 'Hämtar granskning av dashboard',
        error: 'Misslyckades att hämta granskning av dashboard',
      },
      fetchProductionOrder: {
        title: 'Hämtar granskning av tillverkningsorder',
        error: 'Misslyckades att hämta granskning av tillverkningsorder',
      },
    },
  },
  units: {
    millimeters: 'mm',
    cubicMeter: 'm³',
    kilogram: 'kg',
    ton: 'ton',
    kgPerM3: 'kg/m3',
  },
  coils: {
    actions: {
      fetchMany: {
        title: 'Hämtar spolar',
        error: 'Misslyckades att hämta spolar',
      },
      fetch: {
        title: 'Hämtar spole',
        error: 'Misslyckades att hämta spole',
      },
      create: {
        title: 'Skapar spole',
        error: 'Misslyckades att skapa spole',
      },
      update: {
        title: 'Redigerar spole',
        error: 'Misslyckades att redigera spole',
      },
      delete: {
        title: 'Ta bort spole',
        error: 'Misslyckades att ta bort spole',
      },
      undoMove: {
        title: 'Ångra flytt av spole',
        error: 'Misslyckades att ändra flytt av spole',
      },
    },

    deleteDialog: {
      header: (name: string) => `Ta bort spole ${name}`,
      description:
        'Är du säkert på att du vill ta bort den här spolen? Denna åtgärd kan inte ångras.',
      deleteButton: 'Ja, ta bort spole',
    },

    editDialog: {
      create: {
        title: 'Skapa ny spole',
      },
      edit: {
        title: 'Redigera spole',
      },
    },

    page: {
      title: 'Spolar',
      notFound: 'Hittade inga spolar',
      loading: 'Hämtar spolar',
    },
    slots: 'Antal platser',
    slotWidth: 'Plats bredd',
    maxHeight: 'Max höjd',
    maxLevels: 'Max antal nivåer',
    diameter: 'Diameter',
    depth: 'Djup',
    product: 'Produkt',
    storageArea: 'Lagerplats',
    position: 'Position',
    level: 'Nivå',
    maxCoilDiameter: 'Max diameter på spole',

    moveDialog: {
      title: 'Flytta spole',
    },

    unableToDrawCoils: (coils: string[]) => {
      if (coils.length === 1) {
        return `Spolen ${coils[0]} står på en plats som inte är möjlig`;
      } else {
        return 'Flera spolar står på platser som inte är möjliga';
      }
    },
  },
  logYard: {
    moveDialog: {
      title: 'Flytta stockar',
    },
  },
  bulk: {
    actions: {
      fetch: {
        title: 'Hämtar bulk',
        error: 'Misslyckades med att hämta bulk',
      },
      create: {
        title: 'Skapar bulk',
        error: 'Misslyckades med att skapa bulk',
      },
      update: {
        title: 'Uppdaterar bulk',
        error: 'Misslyckades med att uppdatera bulk',
      },
      delete: {
        title: 'Tar bort bulk',
        error: 'Misslyckades med att ta bort bulk',
      },
      undoMove: {
        title: 'Ångra bulkflytt',
        error: 'Misslyckades med att ångra bulkflytt',
      },
    },
    maxWeight: 'Maxvikt',
    maxVolume: 'Maxvolym',

    bulkUnit(value?: Unit | null): string {
      switch (value) {
        case Unit.Kilogram:
          return 'kilogram';
        case Unit.CubicMeter:
          return 'kubikmeter';
        case Unit.LongTon:
          return 'långa ton';
        case Unit.MetricTon:
          return 'ton';
        case Unit.Pound:
          return 'pund';
        case Unit.ShortTon:
          return 'korta ton';
        default:
          return '';
      }
    },
    bulkUnitShort(value?: Unit | null): string {
      switch (value) {
        case Unit.MetricTon:
          return 't';
        case Unit.Kilogram:
          return 'kg';
        case Unit.CubicMeter:
          return 'm³';
        case Unit.LongTon:
          return 'l.tn';
        case Unit.Pound:
          return 'lb';
        case Unit.ShortTon:
          return 'tn';
        default:
          return '';
      }
    },
    moveDialog: {
      title: 'Flytta bulk',
    },
  },
  dashboard: {
    actions: {
      fetchMany: {
        title: 'Hämtar dashboards',
        error: 'Misslyckades att hämta dashboards',
      },
      fetch: {
        title: 'Hämtar dashboard',
        error: 'Misslyckades att hämta dashboard',
      },
      create: {
        title: 'Skapar dashboard',
        error: 'Misslyckades att skapa dashboard',
      },
      update: {
        title: 'Redigerar dashboard',
        error: 'Misslyckades att redigera dashboard',
      },
      delete: {
        title: 'Ta bort dashboard',
        error: 'Misslyckades att ta bort dashboard',
        statusCodes: {
          409: 'Misslyckades att ta bort dashboard',
        },
      },
    },
    listPage: {
      title: 'Dashboards',
      noFound: 'Hittade inga dashboards',
      loading: 'Laddar dashboards...',
    },
    deleteDialog: {
      header: (name: string) => `Ta bort dashboard ${name}`,
      description:
        'Är du säkert på att du vill ta bort den här dashboarden? Denna åtgärd kan inte ångras.',
      deleteButton: 'Ja, ta bort dashboard',
    },
    editPage: {
      editTitle: 'Redigera dashboard',
      createTitle: 'Skapa dashboard',
    },
    editComponentDialog: {
      editTitle: 'Redigera komponent',
      addTitle: 'Lägg till komponent',
    },
    productGroupTable: {
      title: 'Lager per produktgrupp',
    },
    productGroupDiagram: {
      title: 'Lager per produktgrupp',
    },
    productTable: {
      title: 'Lager per produkt',
    },
  },
  overview: {
    title: 'Översikt',
  },
  releaseNotes: {
    actions: {
      fetchLatest: {
        title: 'Hämtar senaste release note',
        error: 'Misslyckades att hämta senaste release note',
      },
      fetchMany: {
        title: 'Hämtar release notes',
        error: 'Misslyckades att hämta release notes',
      },
    },
  },
  keybinds: {
    action: 'Kommando',
    keybind: 'Knapp',
  },
  reportPage: {
    selectReport: 'Välj rapport',
    showReport: 'Visa rapport',
    selectExportFormat: 'Välj exportformat',
    exportReport: 'Exportera rapport',
    exportError: 'Misslyckades att exportera rapport',
    showError: 'Misslyckades att visa rapport',
  },
};
